/**
 * RunningHub 侧栏历史写入 localStorage 前瘦身（方案 B）：
 * 去掉 data:image/ 大 base64，保留 https URL、id、timestamp、thumbUrl 等小字段。
 */

/** 与后端 `uploadBase64ImageToCOS(..., 'runninghub-history-thumb')` 对象键一致；列表只用此类 URL 作预览。 */
export function isRunningHubListThumbUrl(raw: unknown): boolean {
  if (typeof raw !== 'string') return false;
  const u = raw.trim();
  if (!/^https?:\/\//i.test(u)) return false;
  try {
    return new URL(u).pathname.toLowerCase().includes('runninghub-history-thumb');
  } catch {
    return false;
  }
}

const RUNNINGHUB_HISTORY_LS_VERSION_KEY = 'nemo_runninghub_history_ls_version';

/** 升级时清空旧 RunningHub 历史 localStorage，避免旧结构/旧脚本与当前列表策略混用 */
export const RUNNINGHUB_HISTORY_LS_VERSION = 6;

/** 去掉非法 thumbUrl（例如误存的高清 COS 路径），避免列表误拉 /runninghub-physical/ 等 */
export function stripInvalidRunningHubThumbUrlFromPayload<T>(payload: T): T {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload;
  const o = { ...(payload as Record<string, unknown>) };
  if ('thumbUrl' in o && !isRunningHubListThumbUrl(o.thumbUrl)) {
    delete o.thumbUrl;
  }
  return o as T;
}

const RUNNINGHUB_HISTORY_LOCALSTORAGE_KEYS = [
  'upscaleHistory',
  'toneMappingHistory',
  'lightReconstructHistory',
  'textReconstructHistory',
  'physicalReconstructHistory',
] as const;

export function migrateRunningHubHistoryLocalStorageIfNeeded(): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
  try {
    const v = parseInt(localStorage.getItem(RUNNINGHUB_HISTORY_LS_VERSION_KEY) || '0', 10);
    if (v >= RUNNINGHUB_HISTORY_LS_VERSION) return;
    for (const k of RUNNINGHUB_HISTORY_LOCALSTORAGE_KEYS) {
      localStorage.removeItem(k);
    }
    localStorage.setItem(RUNNINGHUB_HISTORY_LS_VERSION_KEY, String(RUNNINGHUB_HISTORY_LS_VERSION));
  } catch {
    /* ignore */
  }
}

export function stripRunningHubPayloadForLocalStorage<T extends Record<string, unknown>>(item: T): T {
  const out = { ...item } as Record<string, unknown>;
  for (const k of Object.keys(out)) {
    const v = out[k];
    if (typeof v === 'string' && v.startsWith('data:image/')) {
      delete out[k];
    }
  }
  return stripInvalidRunningHubThumbUrlFromPayload(out as T);
}

export function stripRunningHubHistoryListForLocalStorage<T extends Record<string, unknown>>(items: T[]): T[] {
  return items.map((x) => stripRunningHubPayloadForLocalStorage(x));
}
